import { Animated, StyleSheet, View } from 'react-native'
import { observer } from 'mobx-react-lite'
import { Player } from '@ppwr/poker'
import { useStore } from '../../../models'
import { PlayerCellProgressBar } from './PlayerCellProgressBar'
import { DS } from '../../../design-system'
import { useEffect, useRef } from 'react'
import { useResponsive } from '@/screens/new-design/hooks'
import { Typography } from '@/design-system/Typography'
import { useRemoteConfig } from '@/modules/remote-config'

interface PlayerCellStatusProps {
  player: Player
  right?: boolean
  lastAction?: string
  bottom?: boolean
}

export const PlayerCellStatus = observer(({ player, right, lastAction, bottom }: PlayerCellStatusProps) => {
  const {
    game: {
      actionTime,
      playerActionTimeLeft,
      shouldShowProgressBar,
      isGameOver,
      currentPlayerId,
      applyOpacityForPlayer
    }
  } = useStore()

  const { verticalScale } = useResponsive()
  const { is_avatar_timer_enabled } = useRemoteConfig()

  const applyOpacity = applyOpacityForPlayer(player)
  const showProgressBar =
    !lastAction &&
    shouldShowProgressBar &&
    player.id === currentPlayerId &&
    !player.isFolded() &&
    !is_avatar_timer_enabled

  const scaleY = useRef(new Animated.Value(0)).current

  useEffect(() => {
    Animated.timing(scaleY, {
      toValue: 1,
      duration: 100,
      useNativeDriver: true
    }).start()
  }, [scaleY])

  if (!(showProgressBar || lastAction?.length)) return null

  return (
    <Animated.View
      style={{
        transform: [{ scaleY }, { translateY: scaleY.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }) }]
      }}
    >
      <View
        style={[
          styles.outerContainer,
          right ? styles.right : {},
          !lastAction && showProgressBar && !isGameOver ? { ...styles.progressBar, height: verticalScale(22) } : {},
          showProgressBar && !isGameOver ? { ...styles.progressBarContainer, padding: verticalScale(5) } : {},
          { opacity: applyOpacity ? 0.4 : 1 },
          bottom && styles.bottomProgressBar
        ]}
      >
        {lastAction ? (
          <DS.Text
            variant="Caption/Bold"
            uppercase
            style={{
              fontSize: verticalScale(Number(Typography['Caption/Bold'].fontSize)),
              lineHeight: verticalScale(Number(Typography['Caption/Bold'].lineHeight))
            }}
          >
            {lastAction}
          </DS.Text>
        ) : showProgressBar && !isGameOver ? (
          <PlayerCellProgressBar duration={playerActionTimeLeft} maxDuration={actionTime} />
        ) : null}
      </View>
    </Animated.View>
  )
})

const styles = StyleSheet.create({
  outerContainer: {
    justifyContent: 'center',
    backgroundColor: DS.palette['Brand/Light Grey'],
    borderTopStartRadius: 2,
    borderTopEndRadius: DS.spacing[12],
    borderBottomLeftRadius: DS.spacing[12],
    borderBottomRightRadius: DS.spacing[12],
    paddingVertical: DS.spacing[4],
    paddingHorizontal: DS.spacing[8]
  },
  progressBarContainer: {
    backgroundColor: 'rgba(255, 114, 34, 1)',
    padding: 5
  },
  right: {
    borderTopStartRadius: DS.spacing[12],
    borderTopEndRadius: 2
  },
  lastActionText: {
    padding: 5
  },
  progressBar: {
    height: 22,
    width: '100%'
  },
  bottomProgressBar: {
    width: 100
  }
})
